import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react';

const SearchableSelect = ({ 
  label, 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select option...",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedLabel = options.find((opt) => opt.value === value)?.label;

  const handleSelect = (selectedValue) => {
    onChange(selectedValue);
    setIsOpen(false);
    setSearchTerm("");
  };

  const showSearch = options.length > 5;

  return (
    <div className="w-full relative" ref={wrapperRef}>
      {label && (
        <label className="text-xs font-semibold text-primary mb-1.5 block">
          {label}
        </label>
      )}

      {/* Trigger Button */}
      <div
        className={`w-full px-3 py-2 bg-white border rounded-md ${disabled ? 'bg-gray-50 cursor-not-allowed text-gray-400' : 'cursor-pointer'} flex items-center justify-between text-sm transition-all
          ${isOpen 
            ? 'border-primary ring-1 ring-primary' // Active border matches theme
            : 'border-gray-300 hover:border-gray-400'
          }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={`truncate ${!selectedLabel ? 'text-gray-400' : 'text-gray-700'}`}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown size={16} className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 flex flex-col">
          
          {/* Search Bar */}
          {showSearch && (
            <div className="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                <input
                  type="text"
                  className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded bg-gray-50 focus:outline-none focus:border-primary focus:bg-white transition-colors"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Options List */}
          <div className="overflow-y-auto flex-1 py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, i) => {
                const isSelected = value === opt.value;
                return (
                  <div
                    title={opt.label}
                    key={i}
                    onClick={() => handleSelect(opt.value)}
                    className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between transition-colors
                      ${isSelected 
                        ? 'bg-primary/10 text-primary font-medium' // Theme based selection
                        : 'text-gray-700 hover:bg-gray-50' // Neutral hover
                      }
                    `}
                  >
                    <span className="truncate mr-2">{opt.label}</span>
                    {isSelected && <Check size={14} className="text-primary" />}
                  </div>
                );
              })
            ) : (
              <div className="px-3 py-4 text-center text-sm text-gray-400">
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;